// Intermission (docs/04 §9): digest summary → proposition → draft → shop → feed → continue.
import { B } from '../content/balance';
import { PAGE_PREFIX } from '../content/ledger';
import { CHARM_LIST, CHARMS, DEALS, LEDGER_PAGES, SYMBOL_LIST, SYMBOLS } from '../content/registry';
import { D, type Decimal } from './decimal';
import { say, sayScript, tip } from './dialogue';
import { newInstance } from './grid';
import { dealDue, rollDeal } from './deals';
import { ringPhone } from './ending';
import type { Modifiers } from './modifiers';
import { allPatternInstances } from './patterns';
import { reckoningPreview } from './reckoning';
import { stream, type Rng } from './rng';
import { priceOf, tribute } from './tribute';
import type { GameEvent, GameState, IntermissionState, MetaState, PatternId, Rarity, RunState, ShopService } from './types';
import { startWindow } from './window';

const RARITIES: Rarity[] = ['C', 'U', 'R', 'L'];

export function nextTribute(run: RunState, mods: Modifiers): Decimal {
  return tribute(run.window.n + 1, mods);
}

export function draftWeights(n: number, rarityBoost: number): Record<Rarity, number> {
  const w = {} as Record<Rarity, number>;
  for (const r of RARITIES) {
    const [a, b] = B.DRAFT_W[r];
    let v = Math.max(0, a + b * n);
    if (r === 'R' || r === 'L') v *= 1 + rarityBoost;
    w[r] = v;
  }
  return w;
}

export function draftPool(mods: Modifiers) {
  return SYMBOL_LIST.filter((d) => d.rarity && !d.noDraft && mods.symbolGates.has(d.gate));
}

export function rollDraft(run: RunState, mods: Modifiers, rng: Rng, opts: { rareOnly?: boolean; extra?: number } = {}): string[] {
  let pool = draftPool(mods);
  if (opts.rareOnly) {
    const rare = pool.filter((d) => d.rarity === 'R' || d.rarity === 'L');
    if (rare.length) pool = rare;
  }
  const weights = draftWeights(run.window.n, mods.rarityBoost);
  const out: string[] = [];
  const choices = mods.draftChoices + (opts.extra ?? 0);
  for (let i = 0; i < choices && out.length < pool.length; i++) {
    const avail = pool.filter((d) => !out.includes(d.id));
    const idx = rng.weighted(avail.map((d) => (weights[d.rarity!] / countOf(avail, d.rarity!)) * (d.sigil ? B.SIGIL_DRAFT_WEIGHT * mods.sigilDraftX : 1)));
    if (idx < 0) break;
    out.push(avail[idx]!.id);
  }
  return out;
}

function countOf(list: { rarity: Rarity | null }[], r: Rarity): number {
  return list.filter((d) => d.rarity === r).length || 1;
}

/** The next unfound Ledger page for the current act, or null. */
export function nextLedgerPage(meta: MetaState, mods: Modifiers): string | null {
  const page = LEDGER_PAGES.find((p) => !meta.lifetime.ledgerPages.includes(p.id));
  if (!page) return null;
  return mods.features.has(page.act) ? page.id : null;
}

/** Ledger pages hide among draft options once Reckonings have begun (the first one is guaranteed). */
function maybeLedger(state: GameState, mods: Modifiers, options: string[], rng: Rng, guaranteed: boolean): string[] {
  const meta = state.meta;
  if (meta.lifetime.reckoningsBeaten === 0 || options.length === 0) return options;
  const page = nextLedgerPage(meta, mods);
  if (!page) return options;
  if (!guaranteed && !rng.chance(B.LEDGER_PAGE_CHANCE)) return options;
  const out = [...options];
  out[rng.int(out.length)] = `${PAGE_PREFIX}${page}`;
  return out;
}

export function shopEligibleCharms(run: RunState, mods: Modifiers) {
  return CHARM_LIST.filter((c) => !c.cursed && !run.charms.includes(c.id) && (!c.requires || c.requires(mods)));
}

function rollShop(run: RunState, mods: Modifiers, rng: Rng): (string | null)[] {
  const out: (string | null)[] = [];
  for (let i = 0; i < mods.shopCharms; i++) {
    const avail = shopEligibleCharms(run, mods).filter((c) => !out.includes(c.id));
    if (!avail.length) break;
    const idx = rng.weighted(avail.map((c) => B.CHARM_ODDS[c.rarity] / countOf(avail, c.rarity)));
    out.push(avail[idx]!.id);
  }
  return out;
}

function rollHymn(run: RunState, mods: Modifiers, rng: Rng): PatternId | null {
  const ids = [...new Set(allPatternInstances(run.size).filter((p) => mods.patterns.has(p.id) && !p.jackpot).map((p) => p.id))];
  return ids.length ? rng.pick(ids) : null;
}

export function openIntermission(
  state: GameState,
  mods: Modifiers,
  early: number,
  earlyBonus: Decimal,
  souls: number,
  events: GameEvent[],
  reckoning?: IntermissionState['reckoning'],
): void {
  const run = state.run!;
  run.phase = 'intermission';
  run.spin = null;
  run.flags.intermissions = (run.flags.intermissions ?? 0) + 1;
  const draftRng = stream(run.rng, 'draft');
  let draft = null;
  if (mods.features.has('draft')) {
    const firstPage = !!reckoning && state.meta.lifetime.ledgerPages.length === 0;
    const extra = run.flags.draftBonus ?? 0;
    delete run.flags.draftBonus;
    const options = maybeLedger(state, mods, rollDraft(run, mods, draftRng, { rareOnly: !!reckoning, extra }), draftRng, firstPage);
    draft = { options, rerolls: 0, done: false, rareOnly: !!reckoning };
  }
  let shop = null;
  if (mods.features.has('shop')) {
    const rng = stream(run.rng, 'shop');
    const hymn = rollHymn(run, mods, rng);
    const charms = rollShop(run, mods, rng);
    // A beaten Reckoning leaves something cursed on the counter (docs/04 §10: † charms).
    if (reckoning) {
      const cursed = CHARM_LIST.filter((c) => c.cursed && !run.charms.includes(c.id) && (!c.requires || c.requires(mods)));
      if (cursed.length) charms.push(rng.pick(cursed).id);
    }
    shop = {
      charms,
      hymn: hymn ? { pattern: hymn, bought: false } : null,
      rerolls: 0,
      bought: { spin: 0, hold: 0, nudge: 0 },
      freeCharms: run.freeCharms,
    };
    run.freeCharms = 0;
  }
  const deal = dealDue(state, mods, !!reckoning) ? rollDeal(state, mods, stream(run.rng, 'shop')) : null;
  const preview = reckoningPreview(run, mods);
  const im = { n: run.window.n, souls, early, earlyBonus, draft, shop, deal, preview, reckoning, flags: {}, phone: null };
  run.intermission = im;
  events.push({ e: 'INTERMISSION_OPEN', n: run.window.n });
  ringPhone(state, im, events);
  if (deal) {
    events.push({ e: 'DEAL_OFFER', offers: deal.offers, forced: !!deal.forced });
    if (deal.forced) sayScript(state, 'loan', events, 'pleased');
    else if (deal.offers.some((id) => DEALS.get(id)?.covenant)) sayScript(state, 'covenantOffer', events, 'adoring');
    else say(state, 'dealOffer', events, undefined, { force: true });
  }
  if (preview.length && !deal) say(state, 'associate', events, undefined, { force: true });
  if (draft) {
    events.push({ e: 'DRAFT_OFFER', options: draft.options });
    tip(state, 'draft', events);
    if (!deal) say(state, 'draft', events);
  }
  if (mods.features.has('feed')) tip(state, 'feed', events);
  if (shop) {
    tip(state, 'shop', events);
    if (!deal) say(state, 'shop', events);
  }
}

// ── Draft ─────────────────────────────────────────────────────────────

export function draftPick(state: GameState, index: number, events: GameEvent[]): string | undefined {
  const run = state.run;
  const d = run?.intermission?.draft;
  if (!run || !d || d.done) return 'no draft';
  const option = d.options[index];
  if (!option) return 'bad index';
  if (option.startsWith(PAGE_PREFIX)) {
    const page = option.slice(PAGE_PREFIX.length);
    if (!LEDGER_PAGES.some((p) => p.id === page)) return 'bad page';
    d.done = true;
    if (!state.meta.lifetime.ledgerPages.includes(page)) state.meta.lifetime.ledgerPages.push(page);
    events.push({ e: 'PAGE_FOUND', id: page });
    say(state, 'page', events, undefined, { force: true });
    return undefined;
  }
  if (!SYMBOLS.has(option)) return 'bad index';
  const inst = newInstance(run, option);
  run.deck.push(inst);
  d.done = true;
  if (!state.meta.almanac.symbols.includes(option)) state.meta.almanac.symbols.push(option);
  events.push({ e: 'DRAFT_PICKED', defId: option });
  events.push({ e: 'SYMBOL_ADDED', uid: inst.uid, defId: option, by: 'draft' });
  return undefined;
}

export function draftSkip(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  const d = run?.intermission?.draft;
  if (!run || !d || d.done) return 'no draft';
  d.done = true;
  run.soulsPending += mods.skipDraftSouls;
  events.push({ e: 'DRAFT_SKIPPED', souls: mods.skipDraftSouls });
  return undefined;
}

export function draftRerollPrice(run: RunState, mods: Modifiers): Decimal {
  const d = run.intermission?.draft;
  return priceOf(B.DRAFT_REROLL, nextTribute(run, mods)).mul(Math.pow(2, d?.rerolls ?? 0));
}

export function draftReroll(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  const d = run?.intermission?.draft;
  if (!run || !d || d.done) return 'no draft';
  const price = draftRerollPrice(run, mods);
  if (run.coins.lt(price)) return 'not enough coins';
  run.coins = run.coins.sub(price);
  d.rerolls++;
  d.options = rollDraft(run, mods, stream(run.rng, 'draft'), { rareOnly: d.rareOnly });
  events.push({ e: 'DRAFT_OFFER', options: d.options });
  return undefined;
}

// ── Shop ──────────────────────────────────────────────────────────────

const freeAll = (run: RunState): boolean => !!run.intermission?.shop?.freeAll;

export function charmPrice(run: RunState, mods: Modifiers, id: string): Decimal {
  const def = CHARMS.get(id)!;
  return priceOf(B.CHARM_PRICE * B.CHARM_TIER[def.rarity] * (1 - mods.shopDiscount), nextTribute(run, mods));
}

export function servicePrice(run: RunState, mods: Modifiers, service: ShopService): Decimal {
  if (freeAll(run)) return D(0);
  const frac = service === 'spin' ? B.EXTRA_SPIN : B.EXTRA_CHARGE;
  return priceOf(frac * (1 - mods.shopDiscount), nextTribute(run, mods));
}

export function hymnPrice(run: RunState, mods: Modifiers): Decimal {
  if (freeAll(run)) return D(0);
  return priceOf(B.HYMN_PRICE * (1 - mods.shopDiscount), nextTribute(run, mods));
}

export function shopRerollPrice(run: RunState, mods: Modifiers): Decimal {
  return priceOf(B.SHOP_REROLL, nextTribute(run, mods)).mul(Math.pow(2, run.intermission?.shop?.rerolls ?? 0));
}

/** What buying the charm in this shop costs right now (free charms from Keys or the Skeleton Key). */
export function charmCost(run: RunState, mods: Modifiers, id: string): Decimal {
  const shop = run.intermission?.shop;
  if (shop && (shop.freeAll || shop.freeCharms > 0)) return D(0);
  return charmPrice(run, mods, id);
}

export function shopBuy(state: GameState, mods: Modifiers, slot: number, events: GameEvent[]): string | undefined {
  const run = state.run;
  const shop = run?.intermission?.shop;
  if (!run || !shop) return 'no shop';
  const id = shop.charms[slot];
  if (!id) return 'empty slot';
  if (run.charms.length >= mods.charmSlots) return 'no free slot';
  const price = charmCost(run, mods, id);
  if (run.coins.lt(price)) return 'not enough coins';
  run.coins = run.coins.sub(price);
  if (!shop.freeAll && shop.freeCharms > 0) shop.freeCharms--;
  run.charms.push(id);
  shop.charms[slot] = null;
  if (!state.meta.almanac.charms.includes(id)) state.meta.almanac.charms.push(id);
  events.push({ e: 'CHARM_BOUGHT', id, price: price.toString() });
  return undefined;
}

export function shopBuyHymn(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  const hymn = run?.intermission?.shop?.hymn;
  if (!run || !hymn || hymn.bought) return 'no hymn';
  const price = hymnPrice(run, mods);
  if (run.coins.lt(price)) return 'not enough coins';
  run.coins = run.coins.sub(price);
  hymn.bought = true;
  run.hymns[hymn.pattern] = (run.hymns[hymn.pattern] ?? 0) + 1;
  events.push({ e: 'HYMN_BOUGHT', pattern: hymn.pattern, level: run.hymns[hymn.pattern]! });
  return undefined;
}

export function shopService(state: GameState, mods: Modifiers, service: ShopService, events: GameEvent[]): string | undefined {
  const run = state.run;
  const shop = run?.intermission?.shop;
  if (!run || !shop) return 'no shop';
  if (service === 'spin' && shop.bought.spin >= B.EXTRA_SPIN_MAX) return 'sold out';
  if (service === 'hold' && !mods.features.has('hold')) return 'locked';
  if (service === 'nudge' && !mods.features.has('nudge')) return 'locked';
  const price = servicePrice(run, mods, service);
  if (run.coins.lt(price)) return 'not enough coins';
  run.coins = run.coins.sub(price);
  shop.bought[service]++;
  run.nextWindow[service === 'spin' ? 'spins' : service]++;
  events.push({ e: 'SHOP_UPDATE' });
  return undefined;
}

export function shopReroll(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  const shop = run?.intermission?.shop;
  if (!run || !shop) return 'no shop';
  const price = shopRerollPrice(run, mods);
  if (run.coins.lt(price)) return 'not enough coins';
  run.coins = run.coins.sub(price);
  shop.rerolls++;
  shop.charms = rollShop(run, mods, stream(run.rng, 'shop'));
  events.push({ e: 'SHOP_UPDATE' });
  return undefined;
}

export function sellCharm(state: GameState, mods: Modifiers, index: number, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'intermission') return 'not in intermission';
  const id = run.charms[index];
  if (!id) return 'bad index';
  const price = CHARMS.get(id)?.cursed ? D(0) : charmPrice(run, mods, id).mul(B.CHARM_SELL).floor();
  run.charms.splice(index, 1);
  run.coins = run.coins.add(price);
  events.push({ e: 'CHARM_SOLD', id, price: price.toString() });
  return undefined;
}

export function reorderCharms(state: GameState, order: string[]): string | undefined {
  const run = state.run;
  if (!run) return 'no run';
  const same = order.length === run.charms.length && [...order].sort().join() === [...run.charms].sort().join();
  if (!same) return 'bad order';
  run.charms = [...order];
  return undefined;
}

export function continueRun(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'intermission') return 'not in intermission';
  const deal = run.intermission?.deal;
  if (deal && !deal.done) return 'answer the proposition';
  startWindow(state, mods, run.window.n + 1, events);
  return undefined;
}
